'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreateJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (jobId: string) => void;
}

type SourceKey =
  | 'website' | 'google_ads' | 'facebook_ads' | 'yard_sign' | 'door_to_door' | 'referral' | 'past_customer' | 'storm_chaser' | 'insurance_claim' | 'other';

type ServiceKey =
  | 'roof_replacement' | 'roof_repair' | 'inspection' | 'storm_claim' | 'gutter' | 'skylight' | 'other';

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_OPTIONS: Array<{ key: SourceKey; label: string }> = [
  { key: 'website',         label: 'Website' },
  { key: 'google_ads',      label: 'Google Ads' },
  { key: 'facebook_ads',    label: 'Facebook Ads' },
  { key: 'yard_sign',       label: 'Yard Sign' },
  { key: 'door_to_door',    label: 'Door-to-Door' },
  { key: 'referral',        label: 'Referral' },
  { key: 'past_customer',   label: 'Past Customer' },
  { key: 'storm_chaser',    label: 'Storm Chaser' },
  { key: 'insurance_claim', label: 'Insurance Claim' },
  { key: 'other',           label: 'Other' },
];

const SERVICE_OPTIONS: Array<{ key: ServiceKey; label: string }> = [
  { key: 'roof_replacement', label: 'Full Roof Replacement' },
  { key: 'roof_repair',      label: 'Roof Repair' },
  { key: 'inspection',       label: 'Inspection Only' },
  { key: 'storm_claim',      label: 'Storm/Hail Damage Claim' },
  { key: 'gutter',           label: 'Gutter Work' },
  { key: 'skylight',         label: 'Skylight Install' },
  { key: 'other',            label: 'Other' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Loads the new Google Maps JS API with the 'places' library (v=beta).
 * PlaceAutocompleteElement requires the new Places API (v=beta or v=weekly).
 */
function loadGoogleMapsScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('SSR'));

    // Already fully loaded with new Places API
    if ((window as any).google?.maps?.places?.PlaceAutocompleteElement) {
      resolve();
      return;
    }

    const SCRIPT_ID = 'google-maps-new-places-script';

    // Script tag already injected — wait for it
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Script load error')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    // Use v=beta to get PlaceAutocompleteElement; loading=async is best practice
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? ''}&libraries=places&v=beta&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateJobModal = ({ isOpen, onClose, onCreated }: CreateJobModalProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [serviceType, setServiceType] = useState<ServiceKey>('roof_replacement');
  const [source, setSource] = useState<SourceKey>('website');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitLabel, setSubmitLabel] = useState('Saving…');
  const [geocodeStatus, setGeocodeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [geocodeResult, setGeocodeResult] = useState<{
    formatted_address: string;
    city: string;
    state: string;
    county: string;
    zip: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [mapsReady, setMapsReady] = useState(false);

  // Container div where PlaceAutocompleteElement will be mounted
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  // Reference to the PlaceAutocompleteElement web component instance
  const placeAutocompleteRef = useRef<any>(null);
  // Listener cleanup function
  const listenerCleanupRef = useRef<(() => void) | null>(null);

  const supabase = createClient();
  const { currentUser } = useAuth();

  // Validation
  const isValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length >= 2 &&
    phone.trim().length >= 7 &&
    address.trim().length >= 8 &&
    !!serviceType &&
    !!source;

  // Load Google Maps script when modal opens
  useEffect(() => {
    if (!isOpen) return;
    loadGoogleMapsScript()
      .then(() => setMapsReady(true))
      .catch((err) => {
        console.warn('Google Maps failed to load:', err);
        setMapsReady(false);
      });
  }, [isOpen]);

  // Mount PlaceAutocompleteElement once script is ready
  const initPlaceAutocomplete = useCallback(() => {
    if (!mapsReady || !autocompleteContainerRef.current) return;
    if (placeAutocompleteRef.current) return; // already mounted

    const google = (window as any).google;
    if (!google?.maps?.places?.PlaceAutocompleteElement) {
      console.warn('PlaceAutocompleteElement not available');
      return;
    }

    // Create the new PlaceAutocompleteElement web component
    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
      types: ['address'],
      componentRestrictions: { country: 'us' },
    });

    // Style the web component to match the rest of the form
    placeAutocomplete.style.width = '100%';
    placeAutocomplete.style.display = 'block';

    // Clear the container and append the element
    autocompleteContainerRef.current.innerHTML = '';
    autocompleteContainerRef.current.appendChild(placeAutocomplete);
    placeAutocompleteRef.current = placeAutocomplete;

    // Listen for place selection
    const handlePlaceChanged = async (event: any) => {
      try {
        const place = event.place;
        if (!place) return;

        // Fetch address fields
        await place.fetchFields({
          fields: ['formattedAddress', 'addressComponents', 'location'],
        });

        const formattedAddress = place.formattedAddress ?? '';
        const components: Record<string, string> = {};

        for (const comp of place.addressComponents ?? []) {
          for (const type of comp.types ?? []) {
            components[type] = comp.longText ?? comp.long_name ?? '';
          }
        }

        const result = {
          formatted_address: formattedAddress,
          city:
            components.locality ||
            components.sublocality ||
            components.administrative_area_level_3 ||
            '',
          state: components.administrative_area_level_1 || '',
          county: (components.administrative_area_level_2 || '').replace(/\s+County$/i, ''),
          zip: components.postal_code || '',
          lat: place.location?.lat() ?? 0,
          lng: place.location?.lng() ?? 0,
        };

        setAddress(formattedAddress);
        setGeocodeStatus('success');
        setGeocodeResult(result);
      } catch (err) {
        console.warn('Place fetch error:', err);
      }
    };

    placeAutocomplete.addEventListener('gmp-placeselect', handlePlaceChanged);

    // Sync typed text → address state so isValid updates as user types
    const handleInput = (e: Event) => {
      const val = (e.target as HTMLInputElement)?.value ?? '';
      setAddress(val);
      // Clear validated geocode result when user edits manually
      setGeocodeResult(null);
      setGeocodeStatus('idle');
    };
    placeAutocomplete.addEventListener('input', handleInput);

    listenerCleanupRef.current = () => {
      placeAutocomplete.removeEventListener('gmp-placeselect', handlePlaceChanged);
      placeAutocomplete.removeEventListener('input', handleInput);
    };
  }, [mapsReady]);

  useEffect(() => {
    if (isOpen && mapsReady) {
      // Small delay to ensure the container div is rendered
      const t = setTimeout(() => initPlaceAutocomplete(), 150);
      return () => clearTimeout(t);
    }
  }, [isOpen, mapsReady, initPlaceAutocomplete]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }
      placeAutocompleteRef.current = null;
      if (autocompleteContainerRef.current) {
        autocompleteContainerRef.current.innerHTML = '';
      }
    }
  }, [isOpen]);

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setFirstName('');
        setLastName('');
        setPhone('');
        setEmail('');
        setAddress('');
        setServiceType('roof_replacement');
        setSource('website');
        setNotes('');
        setSubmitting(false);
        setGeocodeStatus('idle');
        setGeocodeResult(null);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, submitting, onClose]);

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);

    // Phase 1 — Geocode (use already-resolved place data if available, else call API)
    setSubmitLabel('Geocoding…');

    let geocoded: {
      formatted_address: string;
      city: string;
      state: string;
      county: string;
      zip: string;
      lat: number;
      lng: number;
    } | null = geocodeResult;

    if (!geocoded) {
      try {
        const geoTimeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
        const geoFetch = (async () => {
          const resp = await fetch('/api/geocode-address', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ address: address.trim() }),
          });
          const data = await resp.json();
          if (!resp.ok || !data.success) return null;
          return {
            formatted_address: data.formatted_address,
            city: data.city ?? '',
            state: data.state ?? '',
            county: data.county ?? '',
            zip: data.zip ?? '',
            lat: data.lat ?? 0,
            lng: data.lng ?? 0,
          };
        })().catch(() => null);

        geocoded = await Promise.race([geoFetch, geoTimeout]);
      } catch {
        geocoded = null;
      }
    }

    if (geocoded) {
      setGeocodeStatus('success');
      setGeocodeResult(geocoded);
    } else {
      setGeocodeStatus('error');
    }

    // Phase 2 — Insert into customer_pipeline
    setSubmitLabel('Saving…');
    const reporterName =
      `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();

    const insertPayload: Record<string, unknown> = {
      customer_first_name: firstName.trim(),
      customer_last_name: lastName.trim(),
      phone: phone.trim(),
      email: email.trim() || '',
      address: geocoded?.formatted_address ?? address.trim(),
      city: geocoded?.city ?? '',
      state: geocoded?.state ?? '',
      zip: geocoded?.zip ?? '',
      referral_source: source,
      service_type: serviceType,
      notes: notes.trim() || '',
      stage: 'design',
      contact_type: 'homeowner',
      vpm_name: reporterName || '',
      qc_name: '',
    };

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Save timed out after 10 seconds.')), 10000)
    );

    const savePromise = supabase
      .from('customer_pipeline')
      .insert(insertPayload)
      .select('job_id')
      .single()
      .then((res) => {
        if (res.error) throw res.error;
        return res.data;
      });

    try {
      const result = await Promise.race([savePromise, timeoutPromise]);
      const newJobId = (result as Record<string, unknown>)?.job_id ?? 'J-???';
      toast.success(
        `Job ${newJobId} created — ${firstName.trim()} ${lastName.trim()}`,
        { duration: 4500, icon: '🔨' }
      );
      onCreated?.(String(newJobId));
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Failed to create job:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Create failed: ${message}`, { duration: 6000 });
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-start justify-center p-4 pt-20 overflow-y-auto"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-job-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-[#E89A3C]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden="true"
              >
                <rect x="3" y="6" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M12 11v6M9 14h6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h3 id="create-job-title" className="text-lg font-bold text-[#1C2E5E]">
                Create New Job
              </h3>
              <p className="text-[12px] text-gray-500 mt-0.5">Capture a new lead in the pipeline</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Form body */}
        <div className="p-6 space-y-4">
          {/* First / Last Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="cj-first-name"
                className="block text-[12px] font-semibold text-gray-700 mb-1.5"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="cj-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                maxLength={60}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
                autoFocus
              />
            </div>
            <div>
              <label
                htmlFor="cj-last-name"
                className="block text-[12px] font-semibold text-gray-700 mb-1.5"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="cj-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
                maxLength={60}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="cj-phone"
                className="block text-[12px] font-semibold text-gray-700 mb-1.5"
              >
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                id="cj-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(937) 555-1234"
                maxLength={20}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
            <div>
              <label
                htmlFor="cj-email"
                className="block text-[12px] font-semibold text-gray-700 mb-1.5"
              >
                Email{' '}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="cj-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                maxLength={100}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
          </div>

          {/* Service Type pills */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Service Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SERVICE_OPTIONS.map((opt) => {
                const active = serviceType === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setServiceType(opt.key)}
                    className={`px-2 py-2 rounded-lg text-[11px] font-bold transition-all ${
                      active
                        ? 'bg-[#E89A3C] text-white ring-2 ring-[#E89A3C]/30'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Property Address — Google PlaceAutocompleteElement */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Property Address <span className="text-red-500">*</span>
            </label>

            {mapsReady ? (
              /* PlaceAutocompleteElement web component container.
                 The element renders its own input + dropdown as a web component,
                 which is NOT clipped by the modal's overflow-y-auto. */
              <div
                ref={autocompleteContainerRef}
                className="place-autocomplete-wrapper"
                style={{ position: 'relative', zIndex: 10000 }}
              />
            ) : (
              /* Fallback plain input while Maps script loads */
              <div className="relative">
                <input
                  id="cj-address-fallback"
                  type="text"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    if (geocodeStatus === 'success') {
                      setGeocodeStatus('idle');
                      setGeocodeResult(null);
                    }
                  }}
                  placeholder="4821 Maple Ridge Dr, Dayton, OH 45429"
                  maxLength={200}
                  autoComplete="off"
                  className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
                />
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="9" r="2.5" />
                </svg>
              </div>
            )}

            <p className="text-[11px] text-gray-400 mt-1">
              Start typing — select a suggestion to auto-fill and validate.
            </p>
          </div>

          {/* Lead Source */}
          <div>
            <label
              htmlFor="cj-source"
              className="block text-[12px] font-semibold text-gray-700 mb-1.5"
            >
              Lead Source <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="cj-source"
                value={source}
                onChange={(e) => setSource(e.target.value as SourceKey)}
                className="w-full pl-3 pr-8 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all appearance-none cursor-pointer"
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label
              htmlFor="cj-notes"
              className="block text-[12px] font-semibold text-gray-700 mb-1.5"
            >
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="cj-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Anything the team should know — gate codes, dog on property, preferred contact times, insurance carrier, etc."
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1">{notes.length}/1000</p>
          </div>

          {/* Geocoding feedback */}
          {geocodeStatus === 'success' && geocodeResult && (
            <div className="rounded-lg bg-green-50 border border-green-100 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-green-700 mb-1">
                ✓ Address Validated
              </p>
              <p className="text-[12px] text-green-800 font-medium">
                {geocodeResult.formatted_address}
              </p>
              <p className="text-[10px] text-green-600 mt-0.5">
                {geocodeResult.county} County, {geocodeResult.state}
              </p>
            </div>
          )}

          {geocodeStatus === 'error' && (
            <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">
                ⚠ Address Not Validated
              </p>
              <p className="text-[12px] text-amber-800">
                We couldn&apos;t validate this address. The job will still be created, but you may
                want to fix the address later.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background:
                isValid && !submitting
                  ? 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)'
                  : '#9ca3af',
              boxShadow:
                isValid && !submitting ? '0 4px 12px rgba(232,154,60,0.3)' : 'none',
            }}
          >
            {submitting && (
              <svg
                className="w-4 h-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeOpacity="0.25"
                  strokeWidth="3"
                />
                <path
                  d="M12 2a10 10 0 0 1 10 10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {submitting ? submitLabel : 'Create Job'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateJobModal;
